import React from "react";
import {browserHistory} from "react-router";
import {Dispatcher} from "flux";
import logger from "../common/Logger"
import AuthService from "./AuthService";

const DEFAULT_MODAL_ID = 'default';
const LOADING_MODAL_ID = 'loading';

class CoreService {
    static dispatcher = new Dispatcher();
    static modalDisplayed = false;
    static loadingModalActive = false;

    /**
     * Time in ms before loading dialog is displayed.
     *
     * @type {number}
     */
    static loadingDelay = 500;

    /**
     * The base path, e.g. https://www.mocks.cloud
     *
     * @return {string}
     */
    static getBasePath() {
        return document.location.protocol + '//' + document.location.hostname + (document.location.port ? ':' + document.location.port : '');
    }

    static navigateTo(destination) {
        logger.debug(`Navigating to ${destination}`);

        if (destination.substr(0, 7) === 'http://' || destination.substr(0, 8) === 'https://') {
            document.location.href = destination;
        } else {
            browserHistory.push(destination);
        }
    }

    static navigateToHome() {
        AuthService.checkAuthenticatedSession(function (valid) {
            if (valid) {
                CoreService.navigateTo('/dashboard');
            } else {
                CoreService.navigateTo('/');
            }
        });
    }

    static navigateToUnauthenticated() {
        AuthService.setPostLoginUrl(browserHistory.location);
        CoreService.showModal('Log in', 'Oops! You need to be logged in to do that.', {
            onHide() {
                CoreService.navigateTo('/login');
            }
        });
    }

    /**
     * Opens a modal dialog, using the `default` modalId.
     *
     * @param title Dialog title
     * @param body Dialog body
     * @param options Custom options:
     *                  options.dialogSize - bootstrap postfix for dialog size, e.g. "sm", "m";
     *                  options.onHide - callback invoked on dialog dismiss
     */
    static showModal(title, body, options) {
        CoreService.showModalWithId(DEFAULT_MODAL_ID, title, body, options);
    }

    /**
     * Opens a modal dialog with a given modal ID.
     *
     * @param modalId the modal ID
     * @param title Dialog title
     * @param body Dialog body
     * @param options Custom options:
     *                  options.dialogSize - bootstrap postfix for dialog size, e.g. "sm", "m";
     *                  options.onHide - callback invoked on dialog dismiss
     */
    static showModalWithId(modalId, title, body, options) {
        if (CoreService.modalDisplayed) {
            CoreService.hideModal(modalId);
        }
        CoreService.modalDisplayed = true;

        options = options ? options : {};

        const fnHide = () => {
            CoreService.modalDisplayed = false;
            if (typeof options.onHide === 'function') {
                options.onHide();
            }
        };

        logger.debug(`Showing modal: ${modalId} (title: ${title})`);

        CoreService.dispatcher.dispatch({
            type: 'modal',
            target: modalId,
            options: {
                open: true,
                hasCloseButton: (typeof options.hasCloseButton === 'undefined' ? true : options.hasCloseButton),
                closeButtonText: (typeof options.closeButtonText === 'undefined' ? 'Close' : options.closeButtonText),
                hasFooter: (typeof options.hasFooter === 'undefined' ? true : options.hasFooter),
                hasSaveButton: (typeof options.hasSaveButton === 'undefined' ? false : options.hasSaveButton),
                saveButtonText: (typeof options.saveButtonText === 'undefined' ? 'Save changes' : options.saveButtonText),
                title: title,
                body: body,
                onConfirm: () => {
                    if (typeof options.onConfirm === 'function') {
                        options.onConfirm();
                    }
                    fnHide();
                },
                onHide: fnHide
            }
        });
    }

    /**
     * Closes dialog
     *
     * @param modalId the modal ID
     */
    static hideModal(modalId = DEFAULT_MODAL_ID) {
        if (CoreService.modalDisplayed) {
            logger.debug(`Hiding modal: ${modalId}`);
            CoreService.modalDisplayed = false;

            CoreService.dispatcher.dispatch({
                type: 'modal',
                target: modalId,
                options: {
                    open: false
                }
            });
        }
    }

    /**
     * Opens loading dialog.
     *
     * @param message Custom message
     * @param options Custom options:
     *                  options.dialogSize - bootstrap postfix for dialog size, e.g. "sm", "m";
     *                  options.progressType - bootstrap postfix for progress bar type, e.g. "success", "warning".
     */
    static showLoadingModal(message, options) {
        CoreService.loadingModalActive = true;

        message = message ? message : 'Loading';
        options = options ? options : {};
        options.hasCloseButton = false;
        options.hasFooter = false;
        options.onHide = () => {
            CoreService.loadingModalActive = false;
        };

        // Open dialog after delay
        CoreService.loadingDialogTimeout = setTimeout(() => {
            CoreService.loadingDialogTimeout = null;

            // check if should be active
            if (CoreService.loadingModalActive) {
                const progressBody = (
                    <div className="progress progress-striped active" style={{marginBottom: 0}}>
                        <div className="progress-bar" style={{width: "100%"}}/>
                    </div>
                );

                CoreService.showModalWithId(LOADING_MODAL_ID, message, progressBody, options);
            }

        }, CoreService.loadingDelay);
    }

    /**
     * Closes loading dialog.
     */
    static hideLoadingModal() {
        // stop the count-down
        if (CoreService.loadingDialogTimeout) {
            clearTimeout(CoreService.loadingDialogTimeout);
            CoreService.loadingDialogTimeout = null;
        }

        if (CoreService.loadingModalActive) {
            CoreService.loadingModalActive = false;
            CoreService.hideModal(LOADING_MODAL_ID);
        }
    }

    /**
     * Show message in passive error box.
     *
     * @param message
     */
    static showPassiveError(message) {
        CoreService.dispatcher.dispatch({
            type: 'warn',
            payload: {
                visible: true,
                message: message
            }
        });
    }

    /**
     * Show message in passive info box.
     *
     * @param message
     */
    static showPassiveInfo(message) {
        CoreService.dispatcher.dispatch({
            type: 'info',
            payload: {
                visible: true,
                message: message
            }
        });
    }
}

export default CoreService;



// WEBPACK FOOTER //
// ./src/modules/service/CoreService.js